import type {RouteLocation, RouteLocationNormalized} from 'vue-router';

import {UserRoles} from '@/shared/enums/userRolesEnum';
import {useUserStore} from '@/shared/stores/user.store';
import {requireLogin} from '@/shared/utils/routeGuards';

const Dashboard = () => import('@/areas/dashboard/views/Dashboard/DashboardView.vue');

const ImportWizardRoutes = [
   {
      path: 'new',
      name: 'dashboard.lessons.import.wizard.new',
      component: () => import('@/areas/dashboard/views/Lessons/Import/Wizard/ImportWizardStep1View.vue'),
      meta: {
         title: 'Import Resources: Step 1',
         requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
      },
   },
   {
      path: ':id(\\d+)/step-:step(\\d+)',
      name: 'dashboard.lessons.import.wizard.step',
      component: () => import('@/areas/dashboard/views/Lessons/Import/Wizard/ImportWizardStepContainer.vue'),
      props: true,
      meta: {
         title: 'Import Resources: Wizard Step',
         requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
      },
      beforeEnter: (to: RouteLocationNormalized) => {
         const step = parseInt(to.params.step as string);
         if (step >= 2 && step <= 4) {
            to.meta.title = `Import Resources: Step ${step}`;
            return true;
         }
         return {name: 'dashboard.lessons.import.wizard.new'};
      },
   },
];

export const dashboardRoutes = {
   path: '/dashboard',
   component: Dashboard,
   beforeEnter: requireLogin,
   children: [
      {
         name: 'dashboard.index',
         path: '/dashboard',
         component: () => import('@/areas/dashboard/views/Dashboard/DashboardHomeView.vue'),
         meta: {
            title: 'Dashboard',
         },
      },
      {
         name: 'dashboard.users',
         path: '/dashboard/users',
         component: () => import('@/areas/dashboard/views/Users/UsersView.vue'),
         meta: {
            title: 'Users',
         },
      },
      {
         name: 'dashboard.lessons',
         path: '/dashboard/lessons',
         redirect: () => {
            return {path: '/dashboard/lessons/resources'};
         },
         children: [
            {
               name: 'dashboard.lessons.resources',
               path: '/dashboard/lessons/resources',
               component: () => import('@/areas/dashboard/views/Lessons/Resources/ResourcesView.vue'),
               meta: {
                  title: 'Lessons',
               },
            },
            {
               name: 'dashboard.lessons.instructional-sequences',
               path: '/dashboard/lessons/instructional-sequences',
               component: () => import('@/areas/dashboard/views/Lessons/InstructionalSequences/InstructionalSequencesView.vue'),
               meta: {
                  title: 'Instructional Sequences',
               },
            },
            {
               path: '/dashboard/lessons/instructional-sequences/:id(\\d+)',
               name: 'dashboard.lessons.instructional-sequences.view',
               component: () => import('@/areas/dashboard/views/Lessons/InstructionalSequences/PreviewSequenceView.vue'),
               meta: {
                  title: 'Preview Instructional Sequence',
               },
            },
            {
               path: '/dashboard/lessons/instructional-sequences/:id(\\d+)/builder',
               name: 'dashboard.lessons.instructional-sequences.builder',
               component: () => import('@/areas/dashboard/views/Lessons/SequenceBuilder/SequenceBuilderView.vue'),
               meta: {
                  title: 'Instructional Sequence Builder',
               },
            },
            {
               path: '/dashboard/lessons/instructional-sequences/:id(\\d+)/license-codes',
               name: 'dashboard.lessons.instructional-sequences.license-codes',
               component: () => import('@/areas/dashboard/views/Lessons/SequenceLicenseCodes/SequenceLicenseCodesView.vue'),
               meta: {
                  title: 'Instructional Sequence License Codes',
                  requiredRoles: [UserRoles.IT_ADMIN],
               },
            },
            {
               name: 'dashboard.lessons.rubrics',
               path: '/dashboard/lessons/rubrics',
               component: () => import('@/areas/dashboard/views/Lessons/Rubrics/RubricsView.vue'),
               meta: {
                  title: 'Rubrics',
               },
            },
            {
               path: '/dashboard/lessons/rubrics/edit/:id(\\d+)',
               component: () => import('@/areas/dashboard/views/Lessons/Rubrics/RubricEditView.vue'),
               meta: {
                  title: 'Edit Rubric',
               },
            },
            {
               name: 'dashboard.lessons.sequence-categories',
               path: '/dashboard/lessons/sequence-categories',
               component: () => import('@/areas/dashboard/views/Lessons/SequenceCategories/SequenceCategoriesView.vue'),
               meta: {
                  title: 'Sequence Categories',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               path: '/dashboard/lessons/import',
               children: [
                  {
                     path: '',
                     name: 'dashboard.lessons.import',
                     meta: {
                        title: 'Import Resources',
                        requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                     },
                     component: () => import('@/areas/dashboard/views/Lessons/Import/ImportView.vue'),
                  },
                  {
                     path: 'wizard',
                     name: 'dashboard.lessons.import.wizard',
                     redirect: {name: 'dashboard.lessons.import.wizard.new'},
                     children: ImportWizardRoutes,
                  },
               ],
            },
            {
               name: 'dashboard.lessons.meta',
               path: '/dashboard/lessons/meta',
               // No single child (K12 or Workforce) is a valid landing page for
               // every role/sector combination — redirecting to one (e.g.
               // dok-levels, which is K12-only) previously sent Tenant Admin on a
               // Workforce tenant to a page that isn't meant for their sector, and
               // sent a Teacher (who lacks rights on any meta child) bouncing to
               // NotFound at "/" instead of staying on the URL they visited. Render
               // the 404 directly in place instead of picking a fallback child.
               //
               // This must NOT have a `children` array alongside a `component`:
               // Vue Router always renders `children` through the parent
               // component's own <router-view>, even when the children use
               // absolute paths — and Error404View has no <router-view> in its
               // template, which silently broke every meta child page (for
               // every role) the first time this was tried. The former
               // children are now flat siblings below instead.
               component: () => import('@/shared/views/Errors/Error404View.vue'),
            },
            {
               name: 'dashboard.lessons.meta.dok-levels',
               path: '/dashboard/lessons/meta/dok-levels',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/DokLevel/DokLevelView.vue'),
               meta: {
                  title: 'DOK Levels',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.focuses',
               path: '/dashboard/lessons/meta/focuses',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Focus/FocusView.vue'),
               meta: {
                  title: 'Focuses',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.grades',
               path: '/dashboard/lessons/meta/grades',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Grade/GradeView.vue'),
               meta: {
                  title: 'Grades',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.lessons.meta.lesson-numbers',
               path: '/dashboard/lessons/meta/lesson-numbers',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/LessonNumber/LessonNumberView.vue'),
               meta: {
                  title: 'Lesson Numbers',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.lesson-types',
               path: '/dashboard/lessons/meta/lesson-types',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/LessonType/LessonTypeView.vue'),
               meta: {
                  title: 'Lesson Types',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.publishers',
               path: '/dashboard/lessons/meta/publishers',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Publisher/PublisherView.vue'),
               meta: {
                  title: 'Publishers',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.lessons.meta.standards',
               path: '/dashboard/lessons/meta/standards',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Standard/StandardView.vue'),
               meta: {
                  title: 'Standards',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.strand-domains',
               path: '/dashboard/lessons/meta/strand-domains',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/StrandDomain/StrandDomainView.vue'),
               meta: {
                  title: 'Strand Domains',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'k12',
               },
            },
            {
               name: 'dashboard.lessons.meta.subjects',
               path: '/dashboard/lessons/meta/subjects',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Subject/SubjectView.vue'),
               meta: {
                  title: 'Subjects',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.lessons.meta.skills',
               path: '/dashboard/lessons/meta/skills',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/Skill/SkillView.vue'),
               meta: {
                  title: 'Competencies / Skills',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'workforce',
               },
            },
            {
               name: 'dashboard.lessons.meta.job-roles',
               path: '/dashboard/lessons/meta/job-roles',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/JobRole/JobRoleView.vue'),
               meta: {
                  title: 'Job Roles / Functions',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'workforce',
               },
            },
            {
               name: 'dashboard.lessons.meta.content-types',
               path: '/dashboard/lessons/meta/content-types',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/ContentType/ContentTypeView.vue'),
               meta: {
                  title: 'Content Types',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'workforce',
               },
            },
            {
               name: 'dashboard.lessons.meta.proficiency-levels',
               path: '/dashboard/lessons/meta/proficiency-levels',
               component: () => import('@/areas/dashboard/views/Lessons/Meta/ProficiencyLevel/ProficiencyLevelView.vue'),
               meta: {
                  title: 'Proficiency Level',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
                  loginMode: 'workforce',
               },
            },
         ],
      },
      {
         // Orders moved under Organizations; keep the old URL working for bookmarks.
         path: '/dashboard/orders',
         redirect: '/dashboard/organizations/orders',
      },
      {
         name: 'dashboard.students',
         path: '/dashboard/students',
         component: () => import('@/areas/dashboard/views/Students/StudentsView.vue'),
         meta: {
            title: 'Students',
         },
      },
      {
         name: 'dashboard.data',
         path: '/dashboard/data',
         component: () => import('@/areas/dashboard/views/Data/DataView.vue'),
         meta: {
            title: 'Data',
         },
      },
      {
         name: 'dashboard.assignments',
         path: '/dashboard/assignments',
         component: () => import('@/areas/dashboard/views/Assignments/AssignmentsView.vue'),
         meta: {
            title: 'Assignments',
         },
      },
      {
         name: 'dashboard.assignment.students',
         path: '/dashboard/assignments/:id(\\d+)/students',
         component: () => import('@/areas/dashboard/views/Assignments/AssignmentStudentsView.vue'),
         meta: {
            title: 'Assignment Students',
         },
      },
      {
         name: 'dashboard.assignment.student_score',
         path: '/dashboard/assignments/:id(\\d+)/assignments-students/:student_id(\\d+)',
         component: () => import('@/areas/dashboard/views/Assignments/AssignmentScoreStudentView.vue'),
         meta: {
            title: 'Assignment Student Score',
         },
      },
      {
         name: 'dashboard.groups',
         path: '/dashboard/groups',
         component: () => import('@/areas/dashboard/views/Groups/GroupsView.vue'),
         meta: {
            title: 'Groups',
         },
      },
      {
         name: 'dashboard.group.students',
         path: '/dashboard/groups/:id(\\d+)/students',
         component: () => import('@/areas/dashboard/views/Groups/GroupStudentsView.vue'),
         meta: {
            title: 'Group Students',
         },
      },
      {
         name: 'dashboard.organizations',
         path: '/dashboard/organizations',
         // Groups is the one Organizations page a district admin has. The store is read here, at navigation time,
         // not when this file loads, which can be before Pinia is installed.
         redirect: () => {
            const isDistrictAdmin = useUserStore().user?.role === UserRoles.DISTRICT_ADMIN;

            return {path: isDistrictAdmin ? '/dashboard/organizations/groups' : '/dashboard/organizations/districts'};
         },
         children: [
            // Tenant management — IT_ADMIN only
            {
               name: 'dashboard.tenants',
               path: '/dashboard/organizations/tenants',
               component: () => import('@/areas/dashboard/views/Tenants/TenantsView.vue'),
               meta: {
                  title: 'Tenants',
                  requiredRoles: [UserRoles.IT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.new',
               path: '/dashboard/organizations/tenants/new',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCreateView.vue'),
               meta: {
                  title: 'New Tenant',
                  requiredRoles: [UserRoles.IT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.settings',
               path: '/dashboard/organizations/tenants/:id(\\d+)/settings',
               component: () => import('@/areas/dashboard/views/Tenants/TenantSettingsView.vue'),
               props: true,
               meta: {
                  title: 'Tenant Settings',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.branding',
               path: '/dashboard/organizations/tenants/:id(\\d+)/branding',
               component: () => import('@/areas/dashboard/views/Tenants/TenantBrandingView.vue'),
               props: true,
               meta: {
                  title: 'Tenant Branding',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.domains',
               path: '/dashboard/organizations/tenants/:id(\\d+)/domains',
               component: () => import('@/areas/dashboard/views/Tenants/TenantDomainsView.vue'),
               props: true,
               meta: {
                  title: 'Tenant Domains',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.admins',
               path: '/dashboard/organizations/tenants/:id(\\d+)/admins',
               component: () => import('@/areas/dashboard/views/Tenants/TenantAdminsView.vue'),
               props: true,
               meta: {
                  title: 'Tenant Admins',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.pages',
               path: '/dashboard/organizations/tenants/:id(\\d+)/pages',
               component: () => import('@/areas/dashboard/views/Tenants/TenantPagesView.vue'),
               props: true,
               meta: {
                  title: 'Tenant Pages',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.pages.new',
               path: '/dashboard/organizations/tenants/:id(\\d+)/pages/new',
               component: () => import('@/areas/dashboard/views/Tenants/TenantPageEditView.vue'),
               props: true,
               meta: {
                  title: 'New Page',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.tenants.pages.edit',
               path: '/dashboard/organizations/tenants/:id(\\d+)/pages/:pageId(\\d+)',
               component: () => import('@/areas/dashboard/views/Tenants/TenantPageEditView.vue'),
               props: true,
               meta: {
                  title: 'Edit Page',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            // Shared org routes
            {
               name: 'dashboard.organizations.districts',
               path: '/dashboard/organizations/districts',
               component: () => import('@/areas/dashboard/views/Organizations/Districts/DistrictsView.vue'),
               meta: {
                  title: 'Districts',
               },
            },
            {
               name: 'dashboard.organizations.schools',
               path: '/dashboard/organizations/schools',
               component: () => import('@/areas/dashboard/views/Organizations/Schools/SchoolsView.vue'),
               meta: {
                  title: 'Schools',
               },
            },
            // The teacher groups of the schools an admin manages, OneRoster classes included. A teacher's own groups
            // are the top-level Groups page instead.
            {
               name: 'dashboard.organizations.groups',
               path: '/dashboard/organizations/groups',
               component: () => import('@/areas/dashboard/views/Organizations/Groups/GroupsView.vue'),
               meta: {
                  title: 'Groups',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN, UserRoles.DISTRICT_ADMIN],
               },
            },
            // The Reteach groups tab of the same page, and one reteach group, read-only.
            {
               name: 'dashboard.organizations.groups.reteach',
               path: '/dashboard/organizations/groups/reteach',
               component: () => import('@/areas/dashboard/views/Organizations/Groups/GroupsView.vue'),
               meta: {
                  title: 'Reteach Groups',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN, UserRoles.DISTRICT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.groups.reteach.view',
               path: '/dashboard/organizations/groups/reteach/:id(\\d+)',
               component: () => import('@/areas/dashboard/views/Organizations/Groups/ReteachGroupView.vue'),
               meta: {
                  title: 'Reteach Group',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN, UserRoles.DISTRICT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.groups.new',
               path: '/dashboard/organizations/groups/new',
               component: () => import('@/areas/dashboard/views/Organizations/Groups/GroupEditView.vue'),
               meta: {
                  title: 'New Group',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN, UserRoles.DISTRICT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.groups.edit',
               path: '/dashboard/organizations/groups/:id(\\d+)',
               component: () => import('@/areas/dashboard/views/Organizations/Groups/GroupEditView.vue'),
               meta: {
                  title: 'Edit Group',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN, UserRoles.DISTRICT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.license-codes',
               path: '/dashboard/organizations/license-codes',
               component: () => import('@/areas/dashboard/views/Organizations/LicenseCodes/LicenseCodesView.vue'),
               meta: {
                  title: 'License Codes',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.license-codes.edit',
               path: '/dashboard/organizations/license-codes/:id(\\d+)',
               component: () => import('@/areas/dashboard/views/Organizations/LicenseCodes/LicenseCodeEditView.vue'),
               meta: {
                  title: 'Edit License Code',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.orders',
               path: '/dashboard/organizations/orders',
               component: () => import('@/areas/dashboard/views/Orders/OrdersView.vue'),
               meta: {
                  title: 'Orders',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            // Organization settings (self-management)
            {
               name: 'dashboard.organizations.settings',
               path: '/dashboard/organizations/settings',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCurrentSettingsView.vue'),
               meta: {
                  title: 'Settings',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.branding',
               path: '/dashboard/organizations/branding',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCurrentBrandingView.vue'),
               meta: {
                  title: 'Branding',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.domains',
               path: '/dashboard/organizations/domains',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCurrentDomainsView.vue'),
               meta: {
                  title: 'Domains',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.admins',
               path: '/dashboard/organizations/admins',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCurrentAdminsView.vue'),
               meta: {
                  title: 'Admins',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.pages',
               path: '/dashboard/organizations/pages',
               component: () => import('@/areas/dashboard/views/Tenants/TenantCurrentPagesView.vue'),
               meta: {
                  title: 'Pages',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.pages.new',
               path: '/dashboard/organizations/pages/new',
               component: () => import('@/areas/dashboard/views/Tenants/TenantPageEditView.vue'),
               meta: {
                  title: 'New Page',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.pages.edit',
               path: '/dashboard/organizations/pages/:pageId(\\d+)',
               component: () => import('@/areas/dashboard/views/Tenants/TenantPageEditView.vue'),
               meta: {
                  title: 'Edit Page',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            // OneRoster connections. IT_ADMIN sees every connection; a TENANT_ADMIN sees
            // and manages the ones belonging to their own tenant, which the API scopes for
            // them. Registering one stays IT_ADMIN's, so the "new" route below does not
            // widen. The route names share the 'dashboard.organizations.oneroster' prefix on
            // purpose: the navbar highlights the Organizations menu for any route name that
            // starts with the nav item's route.
            {
               name: 'dashboard.organizations.oneroster',
               path: '/dashboard/organizations/oneroster',
               component: () => import('@/views/Rostering/Tenants/List/TenantsListView.vue'),
               meta: {
                  title: 'OneRoster Connections',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            // Single sign-on connections, beside OneRoster because both are organization-level
            // integrations of the same kind: one brings the roster in, the other the people.
            //
            // Unlike OneRoster, a tenant admin registers and removes their own — configuring an
            // identity provider without waiting on the platform team is the point of it, and
            // SsoConnectionPolicy on the backend is what enforces the tenant boundary.
            {
               name: 'dashboard.organizations.sso',
               path: '/dashboard/organizations/sso',
               component: () => import('@/areas/dashboard/views/Sso/SsoListView.vue'),
               meta: {
                  title: 'Single Sign-On',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.sso.new',
               path: '/dashboard/organizations/sso/new',
               component: () => import('@/areas/dashboard/views/Sso/SsoEditView.vue'),
               meta: {
                  title: 'New Sign-In Connection',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.sso.edit',
               path: '/dashboard/organizations/sso/:id(\\d+)',
               component: () => import('@/areas/dashboard/views/Sso/SsoEditView.vue'),
               props: true,
               meta: {
                  title: 'Edit Sign-In Connection',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               // One screen per step or tab. The wizard and the edit page are two flows over the
               // same screens, told apart by ?setup=1 — without it a refresh mid-setup would drop
               // somebody into the edit flow half-configured.
               name: 'dashboard.organizations.sso.edit.step',
               path: '/dashboard/organizations/sso/:id(\\d+)/:step',
               component: () => import('@/areas/dashboard/views/Sso/SsoEditView.vue'),
               props: true,
               meta: {
                  title: 'Edit Sign-In Connection',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.oneroster.new',
               path: '/dashboard/organizations/oneroster/new',
               component: () => import('@/views/Rostering/Tenants/Edit/TenantsEditView.vue'),
               meta: {
                  title: 'New OneRoster Connection',
                  requiredRoles: [UserRoles.IT_ADMIN],
               },
            },
            {
               name: 'dashboard.organizations.oneroster.edit',
               path: '/dashboard/organizations/oneroster/:tenant_id(\\d+)',
               component: () => import('@/views/Rostering/Tenants/Edit/TenantsEditView.vue'),
               meta: {
                  title: 'Edit OneRoster Connection',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
               props: true,
            },
            // One district of a connection, by its own row id: every district the server has
            // now keeps a row, so nothing needs a remote identifier in the address. Declared
            // before the generic ":step" route, which would otherwise swallow "districts/<id>".
            {
               name: 'dashboard.organizations.oneroster.district',
               path: '/dashboard/organizations/oneroster/:tenant_id(\\d+)/districts/:district_id(\\d+)',
               component: () => import('@/views/Rostering/Tenants/Edit/ConnectionDistrictView.vue'),
               meta: {
                  title: 'OneRoster District',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
               props: true,
            },
            {
               name: 'dashboard.organizations.oneroster.edit.step',
               path: '/dashboard/organizations/oneroster/:tenant_id(\\d+)/:step',
               component: () => import('@/views/Rostering/Tenants/Edit/TenantsEditView.vue'),
               meta: {
                  title: 'Edit OneRoster Connection',
                  requiredRoles: [UserRoles.IT_ADMIN, UserRoles.TENANT_ADMIN],
               },
               props: true,
            },
         ],
      },
      // Old address of the OneRoster connections pages (a top-level "Rostering" menu).
      // Kept as redirects so saved links keep working.
      {
         path: '/dashboard/rostering',
         redirect: '/dashboard/organizations/oneroster',
      },
      {
         path: '/dashboard/rostering/tenants',
         redirect: '/dashboard/organizations/oneroster',
      },
      {
         path: '/dashboard/rostering/tenants/:rest(.*)',
         redirect: (to: RouteLocation) => ({path: `/dashboard/organizations/oneroster/${to.params.rest}`}),
      },
   ],
};
